import { useCallback, useMemo, useState } from 'react'

import { ConnectFooter } from '~/app/connect/components/ConnectFooter'
import { ListItemProps } from '~/components/component-library/List/types'
import { SecondaryDisplayButtons } from '~/components/txn-display/SecondaryDisplayButtons'
import { TransactionFailedMessage } from '~/components/txn-display/TransactionFailedMessage'
import { TransactionHeader } from '~/components/txn-display/TransactionHeader'
import { TransactionActions } from '~/components/txn-display/action'
import { BatchOperationsSection } from '~/components/txn-display/batch/BatchOperationsSection'
import { ContractCallSection } from '~/components/txn-display/calls/ContractCallSection'
import { UnknownContractCall } from '~/components/txn-display/calls/UnknownContractCall'
import { buildDisplayCallListItems } from '~/components/txn-display/calls/buildDisplayCallListItems'
import { getCustomCallData } from '~/components/txn-display/calls/getCustomCallData'
import { TransactionCostDisplay } from '~/components/txn-display/cost-display'
import { TransactionMemo } from '~/components/txn-display/memo'
import { TransactionProperties } from '~/components/txn-display/properties'
import { MerkleStateChangeDisplay } from '~/components/txn-display/state-change/components/MerkleStateChangeDisplay'
import { PastStateChangeFooter } from '~/components/txn-display/state-change/components/PastStateChangeFooter'
import { StateChangeErrorMessage } from '~/components/txn-display/state-change/components/StateChangeErrorMessage'
import { StateChangeList } from '~/components/txn-display/state-change/components/StateChangeList'
import { useTransactionStateChanges } from '~/components/txn-display/state-change/hooks/useTransactionStateChanges'
import {
  ERC20StateChangeItem,
  OfframpDisplayData,
} from '~/components/txn-display/state-change/types'
import { TransactionTimeline } from '~/components/txn-display/timeline'
import { TransactionDisplayProps } from '~/components/txn-display/types'
import { getBridgeTokenType } from '~/constants/addresses'
import { useActiveOrg } from '~/hooks/org'
import { BankTransferData, IAddress, MetadataType } from '~/types'
import { isListedChainId } from '~/utils'
import { Address } from '~/utils/address'
import { isBatchTransaction } from '~/utils/smartAccounts/batch'
import { cn } from '~/utils/tailwind-utils'
import { getTxnDetailsUrl } from '~/utils/tenderly'

export const TransactionDisplay = ({
  transaction,
  onClose,
  onConfirmation,
  onSubmitError,
  includeTitle = false,
  includeTitleAndIcon = false,
  fixButtonsPosition = false,
  showTransactionActions = true,
  allowRemoveBatchOperation = false,
  removingBatchOperationId,
  onRemoveBatchOperation,
}: TransactionDisplayProps) => {
  const [isShowingCalls, setIsShowingCalls] = useState(false)
  const handleToggleCalls = useCallback(
    () => setIsShowingCalls((prev) => !prev),
    [],
  )

  const chainId = isListedChainId(transaction.chainId)
    ? transaction.chainId
    : undefined
  const smartAccountAddress = transaction.smartAccountAddress

  const { org } = useActiveOrg()

  const {
    stateChangeItems,
    isLoading: isLoadingStateChanges,
    hasError,
    hasNoTransfers,
    errorMessage,
    isSimulationRevert,
    isPending,
    isMerkle,
    typedTransaction,
    runTenderlySimulation,
    simulationUrl,
    decodedCalls,
    failingCallIndexes,
  } = useTransactionStateChanges({ transaction })

  // Build offramp display data based on metadata (works for public pages too)
  const offrampData: OfframpDisplayData | undefined = useMemo(() => {
    // Check for bank_transfer metadata - if not present, not an offramp
    const metadata =
      typedTransaction &&
      'transactionMetadata' in typedTransaction &&
      Array.isArray(typedTransaction.transactionMetadata)
        ? typedTransaction.transactionMetadata
        : undefined
    const bankTransferMetadata = metadata?.find(
      (m): m is BankTransferData & { type: MetadataType.BankTransfer } =>
        m.type === MetadataType.BankTransfer,
    )
    if (!bankTransferMetadata) return undefined

    // Find the inflow state change item (the one going to the liquidation address)
    const inflowItem = stateChangeItems.find(
      (item): item is ERC20StateChangeItem =>
        item.state_change_type === 'erc-20' && item.direction === 'inflow',
    )
    if (!inflowItem) return undefined

    // Try to find the matching bank account (may have been removed)
    const bankAccount = org?.bridge?.externalAccounts?.find(
      ({ liquidationAddress }) =>
        liquidationAddress.toLowerCase() === inflowItem.address.toLowerCase(),
    )

    // Determine currency from the token
    const tokenType = getBridgeTokenType(
      inflowItem.chainId,
      Address.format(inflowItem.token),
    )
    const currency = tokenType === 'eurc' ? 'EUR' : 'USD'

    const feePercent = bankTransferMetadata.feePercent

    // Calculate fee from token amount (not usdValue) so EUR offramps show fee in EUR
    const decimals = inflowItem.tokenMetadata?.decimals ?? 6
    const absoluteAmount =
      inflowItem.amount >= 0n ? inflowItem.amount : -inflowItem.amount
    const tokenAmount = Number(absoluteAmount) / 10 ** decimals

    return {
      bankName: bankAccount?.name ?? 'Bank account (removed)',
      liquidationAddress: inflowItem.address as IAddress,
      processingFee: tokenAmount * (feePercent / 100),
      processingFeeRate: feePercent / 100,
      currency,
    }
  }, [org?.bridge?.externalAccounts, stateChangeItems, typedTransaction])

  const customCallData = getCustomCallData({ transaction: typedTransaction })
  const batchOperations = isBatchTransaction(transaction)
    ? (transaction.batchOperations ?? [])
    : []
  const shouldShowBatchOperations = batchOperations.length > 0

  const callDataItems: ListItemProps[] = useMemo(() => {
    const calls = typedTransaction?.calls
    // Only show contract calls if we have custom call data (typed transaction)
    if (!customCallData?.contractAddress) return []
    if (!calls?.length || !chainId || !smartAccountAddress) return []

    return buildDisplayCallListItems({
      calls: calls.map((call, index) => ({
        ...call,
        functionSignature:
          index === 0 ? customCallData.functionSignature : undefined,
        contractName: index === 0 ? customCallData.contractName : undefined,
      })),
      chainId,
      smartAccountAddress,
      decodedCalls,
    })
  }, [
    typedTransaction?.calls,
    chainId,
    smartAccountAddress,
    customCallData,
    decodedCalls,
  ])

  // Determine what sections to show
  const shouldShowContractCall =
    !isMerkle &&
    !shouldShowBatchOperations &&
    Boolean(smartAccountAddress && chainId) &&
    (callDataItems.length > 0 || hasNoTransfers || hasError)

  const shouldShowStateChangeSection =
    !isMerkle &&
    !hasError &&
    // Don't show loading skeleton when there's custom call data - only show when items exist
    ((callDataItems.length === 0 && isLoadingStateChanges) ||
      stateChangeItems.length > 0)

  // Build UI elements
  const tenderlyUrl =
    transaction.transactionHash && chainId
      ? getTxnDetailsUrl({
          chainId,
          txnHash: transaction.transactionHash,
          userOpHash: transaction.userOpHash,
        })
      : undefined

  const contractAddress =
    customCallData?.contractAddress ??
    (typedTransaction?.calls?.[0]?.to as IAddress | undefined)

  const secondaryDisplayButtons = (
    <SecondaryDisplayButtons
      isShowingCalls={isShowingCalls}
      onToggleCalls={handleToggleCalls}
      hasDecodedCalls={!!decodedCalls?.length}
      runTenderlySimulation={isPending ? runTenderlySimulation : undefined}
      simulationUrl={isPending ? simulationUrl : undefined}
      tenderlyUrl={tenderlyUrl}
    />
  )

  const transactionActions = (
    <TransactionActions
      transaction={transaction}
      onClose={onClose}
      onConfirmation={onConfirmation}
      onSubmitError={onSubmitError}
      isSimulationRevert={isSimulationRevert}
      simulationRevertReason={isSimulationRevert ? errorMessage : undefined}
    />
  )

  return (
    <div
      className={cn('flex w-full flex-col gap-4', fixButtonsPosition && 'pb-6')}
    >
      {(includeTitle || includeTitleAndIcon) && (
        <TransactionHeader
          transaction={transaction}
          showIcon={includeTitleAndIcon}
        />
      )}
      <TransactionMemo transaction={transaction} />
      <TransactionFailedMessage transaction={transaction} />
      {shouldShowBatchOperations && chainId && smartAccountAddress && (
        <BatchOperationsSection
          batchOperations={batchOperations}
          chainId={chainId}
          smartAccountAddress={smartAccountAddress}
          decodedCalls={decodedCalls}
          failingCallIndexes={failingCallIndexes}
          allowRemove={allowRemoveBatchOperation}
          removingOperationId={removingBatchOperationId}
          onRemoveOperation={onRemoveBatchOperation}
          tenderlyUrl={tenderlyUrl}
          simulationUrl={isPending ? simulationUrl : undefined}
          runTenderlySimulation={isPending ? runTenderlySimulation : undefined}
        />
      )}
      {shouldShowContractCall && callDataItems.length > 0 && (
        <ContractCallSection
          items={callDataItems}
          decodedCalls={decodedCalls}
          isShowingCalls={isShowingCalls}
          secondaryDisplayButtons={secondaryDisplayButtons}
          label={
            callDataItems.length === 1 ? 'Contract call' : 'Contract calls'
          }
        />
      )}
      {shouldShowContractCall &&
        callDataItems.length === 0 &&
        smartAccountAddress &&
        chainId && (
          <UnknownContractCall
            smartAccountAddress={smartAccountAddress}
            chainId={chainId}
            contractAddress={contractAddress}
            decodedCalls={decodedCalls}
            isShowingCalls={isShowingCalls}
            secondaryDisplayButtons={secondaryDisplayButtons}
          />
        )}
      {!isMerkle && hasError && (
        <StateChangeErrorMessage
          errorMessage={errorMessage}
          isSimulationRevert={isSimulationRevert}
        />
      )}
      {isMerkle && <MerkleStateChangeDisplay transaction={transaction} />}
      {shouldShowStateChangeSection && (
        <StateChangeList
          stateChangeItems={stateChangeItems}
          isLoading={isLoadingStateChanges}
          decodedCalls={
            shouldShowContractCall || shouldShowBatchOperations
              ? undefined
              : decodedCalls
          }
          isShowingCalls={isShowingCalls}
          secondaryDisplayButtons={
            shouldShowContractCall || shouldShowBatchOperations
              ? undefined
              : secondaryDisplayButtons
          }
          footer={
            typedTransaction && (
              <PastStateChangeFooter transaction={typedTransaction} />
            )
          }
          offrampData={offrampData}
        />
      )}
      <TransactionTimeline transaction={transaction} />
      <TransactionProperties properties={transaction.properties} />
      <TransactionCostDisplay transaction={transaction} />
      {showTransactionActions &&
        (fixButtonsPosition ? (
          <ConnectFooter stacked>{transactionActions}</ConnectFooter>
        ) : (
          <div className="mt-4">{transactionActions}</div>
        ))}
    </div>
  )
}
